import { Link, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ArrowRight, Trophy, TableIcon, ChevronDown, ChevronUp, RefreshCw } from "lucide-react"
import { useEffect, useState } from "react"
import { TournamentStatusBadge } from "@/components/ui/TournamentStatusBadge"
import { getTournamentImage } from "@/pages/Tournament/Detail/utils"
import { useActiveTournamentsWithRounds } from "@/graphql/hooks/useActiveTournamentsWithRounds"
import type {
  ActiveRound,
  ActiveMatch,
  ActiveTournamentWithRounds,
} from "@/graphql/types/activeTournamentsWithRounds"
import type { Tournament } from "@/graphql/types"

const SKELETON_COUNT = 4

/** Etiqueta del estado de la ronda */
const ROUND_STATUS: Record<
  number,
  { label: string; variant: "default" | "secondary" | "outline" }
> = {
  1: { label: "En curso", variant: "outline" },
  2: { label: "Terminada", variant: "default" },
  3: { label: "Anulada", variant: "secondary" },
}

/** Retorna el nombre del oponente o "Bye" */
function getOpponentName(match: ActiveMatch, personId: number): string {
  if (match.Player?.Person?.id === personId) {
    return match.Opponent?.Person?.fullName ?? "Bye"
  }
  return match.Player?.Person?.fullName ?? "Bye"
}

/** Resultado de la ronda para el usuario */
function getMatchResult(
  match: ActiveMatch,
  personId: number,
): { label: string; color: string } | null {
  if (!match.reported) return null
  if (match.draw) return { label: "Empate", color: "text-yellow-500" }

  const isBye = !match.Opponent
  if (isBye) return { label: "Bye", color: "text-blue-500" }

  const isWinner =
    match.winnerId !== null &&
    ((match.Player?.Person?.id === personId && match.winnerId === match.playerId) ||
      (match.Opponent?.Person?.id === personId && match.winnerId === match.opponentId))
  return isWinner
    ? {
        label: `Ganaste ${match.winnerVictories ?? ""}–${match.loserVictories ?? ""}`,
        color: "text-green-500",
      }
    : {
        label: `Perdiste ${match.loserVictories ?? ""}–${match.winnerVictories ?? ""}`,
        color: "text-red-500",
      }
}

function getRoundStartTimeLabel(round: ActiveRound): string | null {
  if (!round.created) return null
  const date = new Date(round.created)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleTimeString("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
  })
}

function getMatchReportDetail(match: ActiveMatch): string | null {
  if (!match.reported) return null

  const reportedBy = match.ModifiedBy?.fullName?.trim() || "Sistema"
  if (!match.modified) return `Reportado por ${reportedBy}`

  const date = new Date(match.modified)
  if (Number.isNaN(date.getTime())) return `Reportado por ${reportedBy}`

  const timeLabel = date.toLocaleTimeString("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
  })
  return `Reportado ${timeLabel} por ${reportedBy}`
}

/** Panel de rondas de un torneo */
function TournamentRoundsPanel({
  tournament,
  personId,
}: {
  tournament: ActiveTournamentWithRounds
  personId: number
}) {
  const rounds = tournament.TournamentRounds ?? []
  if (rounds.length === 0) return null

  return (
    <div className="mt-2 flex flex-col gap-2 pl-2 border-l-2 border-primary/20">
      {rounds.map((round) => {
        const match = round.Match
        if (!match) return null

        const status = ROUND_STATUS[round.statusId] ?? ROUND_STATUS[1]
        const opponentName = getOpponentName(match, personId)
        const result = getMatchResult(match, personId)
        const roundStartTime = getRoundStartTimeLabel(round)
        const reportDetail = getMatchReportDetail(match)

        return (
          <div
            key={round.id}
            className="flex flex-col gap-1 rounded-lg bg-muted/40 px-3 py-2 text-[11px] sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-1 sm:text-xs"
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
              <span className="font-semibold text-foreground/80">{round.name}</span>
              <Badge variant={status.variant} className="text-[10px] leading-none px-1.5 py-0.5">
                {status.label}
              </Badge>
              <span className="flex items-center gap-1 text-muted-foreground shrink-0">
                <TableIcon className="h-3 w-3" />
                Mesa {match.table}
              </span>
              {roundStartTime && (
                <span className="text-muted-foreground hidden sm:inline">
                  Inicio {roundStartTime}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
              <span className="min-w-0 truncate text-muted-foreground">
                vs <span className="font-medium text-foreground/100">{opponentName}</span>
              </span>
              {result && (
                <span className={`shrink-0 font-semibold ${result.color}`}>{result.label}</span>
              )}
            </div>
            {reportDetail && (
              <span className="text-muted-foreground hidden sm:ml-auto sm:block">
                {reportDetail}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

interface ActiveTournamentsSectionProps {
  personId: number | undefined
}

export const ActiveTournamentsSection = ({ personId }: ActiveTournamentsSectionProps) => {
  const navigate = useNavigate()
  const isLoggedIn = Boolean(personId)
  const { tournaments, loading, refetch } = useActiveTournamentsWithRounds(personId)
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  const [autoExpandDone, setAutoExpandDone] = useState(false)
  const [refreshCooldown, setRefreshCooldown] = useState(0)

  useEffect(() => {
    if (autoExpandDone || !personId || tournaments.length === 0) return
    const firstWithRounds = tournaments.find((t) => (t.TournamentRounds ?? []).some((r) => r.Match))
    if (firstWithRounds) {
      setExpandedIds(new Set([firstWithRounds.id]))
    }
    setAutoExpandDone(true)
  }, [tournaments, personId, autoExpandDone])

  useEffect(() => {
    if (refreshCooldown <= 0) return
    const timeoutId = window.setTimeout(() => {
      setRefreshCooldown((prev) => prev - 1)
    }, 1000)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [refreshCooldown])

  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <section className="px-3 sm:px-4 py-5 sm:py-8 md:py-12">
      <div className="container mx-auto max-w-7xl p-0">
        <div className="mb-4 sm:mb-6 flex flex-col gap-2 sm:gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-xl sm:text-3xl font-bold md:text-4xl">
              {isLoggedIn ? "Tus torneos recientes" : "Torneos activos y recientes"}
            </h2>
            <p className="mt-1 sm:mt-2 max-w-2xl text-xs sm:text-sm text-muted-foreground">
              {isLoggedIn
                ? "Torneos recientes ordenados por fecha. Toca para ver rondas o resultados."
                : "Explora torneos recientes. Inicia sesión para ver tus rondas y oponentes."}
            </p>
            {!isLoggedIn && (
              <div className="mt-2 sm:mt-3 flex flex-wrap gap-2">
                <Link to="/login">
                  <Button size="sm">Iniciar sesión</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" variant="outline">
                    Crear cuenta
                  </Button>
                </Link>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isLoggedIn && (
              <Button
                size="sm"
                variant="outline"
                disabled={loading || refreshCooldown > 0}
                onClick={() => {
                  if (refreshCooldown > 0) return
                  void refetch()
                  setRefreshCooldown(30)
                }}
              >
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                {refreshCooldown > 0 ? `Actualizar (${refreshCooldown}s)` : "Actualizar"}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(isLoggedIn ? "/profile/tournaments" : "/tournaments")}
            >
              Ver todos
              <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: SKELETON_COUNT }, (_, i) => (
              <div key={i} className="animate-pulse h-20 rounded-2xl bg-muted" />
            ))}
          </div>
        ) : tournaments.length > 0 ? (
          <div className="flex flex-col gap-3">
            {tournaments.map((tournament) => {
              const bgImage = getTournamentImage(tournament as unknown as Tournament)
              const isExpanded = expandedIds.has(tournament.id)
              const hasAnyRounds = (tournament.TournamentRounds?.length ?? 0) > 0
              const hasRounds =
                personId !== undefined && (tournament.TournamentRounds ?? []).some((r) => r.Match)
              return (
                <div
                  key={tournament.id}
                  className="group overflow-hidden rounded-2xl border border-border/60 bg-card/80 shadow-sm transition-all duration-300 hover:border-primary/30 hover:shadow-md"
                >
                  {/* Banner row */}
                  <div
                    className="relative flex cursor-pointer items-center gap-3 sm:gap-4 px-3.5 sm:px-4 py-3 transition-all duration-300 hover:-translate-y-0.5"
                    onClick={() => {
                      // al hacer click expandir o navegar segun tenga rondas o no
                      if (hasRounds) toggleExpanded(tournament.id)
                      else if (tournament.urlView) navigate(tournament.urlView)
                    }}
                  >
                    {bgImage && (
                      <div
                        className="pointer-events-none absolute inset-0 hidden opacity-10 bg-cover bg-center transition-opacity duration-300 group-hover:opacity-15 sm:block"
                        style={{ backgroundImage: `url(${bgImage})` }}
                      />
                    )}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-card/90 via-card/60 to-transparent" />

                    {bgImage && (
                      <div className="relative z-10 h-10 w-10 sm:h-12 sm:w-12 shrink-0 overflow-hidden rounded-lg sm:rounded-xl border border-border/60 shadow-sm">
                        <img
                          src={bgImage}
                          alt={tournament.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    )}

                    <div className="relative z-10 min-w-0 flex-1">
                      <p className="truncate font-semibold text-xs sm:text-sm leading-tight group-hover:text-primary transition-colors">
                        {tournament.name}
                      </p>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-0.5 text-[10px] sm:text-xs text-muted-foreground">
                        {tournament.Game?.name && (
                          <span className="font-medium text-primary/80">
                            {tournament.Game.name}
                          </span>
                        )}
                        {tournament.Store?.name && (
                          <span className="truncate">{tournament.Store.name}</span>
                        )}
                        {tournament.startDate && (
                          <span>
                            {new Date(tournament.startDate).toLocaleDateString("es-CL", {
                              day: "2-digit",
                              month: "short",
                            })}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="relative z-10 flex shrink-0 items-center gap-2">
                      <TournamentStatusBadge
                        statusId={tournament.statusId}
                        mini
                        className="hidden md:block"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="hidden sm:flex h-8 gap-1.5 border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (tournament.urlView) navigate(tournament.urlView)
                        }}
                      >
                        {hasAnyRounds ? "Ver resultados" : "Ver información"}
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                      {hasRounds && (
                        <button
                          type="button"
                          aria-label={isExpanded ? "Ocultar rondas" : "Ver rondas"}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleExpanded(tournament.id)
                          }}
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      )}
                      {!hasRounds && (
                        <ArrowRight className="sm:hidden h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      )}
                    </div>
                  </div>

                  {/* Rounds panel */}
                  {isExpanded && personId !== undefined && (
                    <div className="border-t border-border/40 px-3 sm:px-4 pb-2.5 sm:pb-3">
                      <TournamentRoundsPanel tournament={tournament} personId={personId} />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-card/50 py-8 sm:py-12 text-center">
            <Trophy className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
            <p className="text-lg text-muted-foreground">
              {isLoggedIn
                ? "No hay torneos recientes en este momento."
                : "No hay torneos para mostrar en este momento."}
            </p>
            {!isLoggedIn && (
              <div className="mt-4 flex justify-center gap-2">
                <Link to="/login">
                  <Button size="sm">Iniciar sesión</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" variant="outline">
                    Registrarme
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
